import { Checkbox } from "primereact/checkbox";
import { useDispatch } from "react-redux";
import { useAppSelector } from "../../../../../../../stateManagement/hooks/redux";
import { Tree } from "primereact/tree";
import { setCustomMapAppliedFilters, setCustomMapFiltersState } from "../../../../../../../stateManagement/reducers/custom/CustomMapSlice";
import { useEffect } from "react";
import axios from "axios";
import { getItemWebStorage, setItemWebStorage } from "../../../../../../../features/webStorageSaver";
import { configureTreeCheckboxNodes, getAppliedFilterTreeCheckboxValues } from "./customFilterTreeCheckboxFunctions";
import { authorizeToCustomRest } from "../../../../../../../features/customAuth/customAuth";

export default function CustomFilterTreeCheckbox({ fieldName, objectTypeName }: any) {
    const dispatch = useDispatch()
    const filtersState = useAppSelector(state => state.customMap.customMapFiltersState)
    const appliedFilters = useAppSelector(state => state.customMap.customMapAppliedFilters)
    const restAuthToken = useAppSelector(state => state.customAuth.customAuthToken)

    const { treeValues, selectedTreeValues, hasSearch, nullFilterValue, ignoreFilterValue, searchValue } = filtersState[fieldName]

    const captionFilter = (caption: any) => caption.toLowerCase().includes(searchValue.toLowerCase())

    useEffect(() => {
        if (restAuthToken) {
            const savedFiltersState = getItemWebStorage(`customMapFiltersState-${objectTypeName}`)
            if (!savedFiltersState?.[fieldName]?.treeValues) {
                fillInitFilterTreeCheckboxValues(restAuthToken)
            }
        }
    }, [restAuthToken])

    function treeCheckboxHasFilterValue(treeValues: any, searchValue: string, count: number) {
        treeValues && treeValues.map((treeValue: any) => {
            if (searchValue && treeValue.label.toLowerCase().includes(searchValue.toLowerCase())) {
                count++
            }
            if (treeValue.children) {
                count = treeCheckboxHasFilterValue(treeValue.children, searchValue, count)
            }
        })
        return count
    }

    // применение фильтра со значением Tree-Checkbox
    function applyFilterTreeCheckboxValues(selectedTreeValues: any, fieldName: string) {
        let newFiltersState = structuredClone(filtersState)
        newFiltersState[fieldName].selectedTreeValues = selectedTreeValues
        Object.assign(newFiltersState[fieldName].ignoreFilterValue, { checked: false })
        dispatch(setCustomMapFiltersState(newFiltersState))
        setItemWebStorage(`customMapFiltersState-${objectTypeName}`, newFiltersState)
        const { treeValues } = filtersState[fieldName]
        let appliedFiltersTreeValues = { [`ignore-${fieldName}`]: false }
        getAppliedFilterTreeCheckboxValues(treeValues, selectedTreeValues, appliedFiltersTreeValues)
        let newAppliedFilters = {
            ...appliedFilters,
            [fieldName]: {
                ...appliedFilters[fieldName],
                treeValues: {
                    ...appliedFilters[fieldName]?.treeValues,
                    ...appliedFiltersTreeValues
                }
            }
        }
        dispatch(setCustomMapAppliedFilters(newAppliedFilters))
        setItemWebStorage(`customMapAppliedFilters-${objectTypeName}`, newAppliedFilters)
    }

    // применение фильтра со значением Tree-Checkbox
    function applyFilterTreeCheckboxIgnoreOrNullValue(checked: boolean, fieldName: string, type: string) {
        let newFiltersState = structuredClone(filtersState)
        if (type === 'ignore') {
            Object.assign(newFiltersState[fieldName].ignoreFilterValue, { checked: checked })
        }
        else {
            Object.assign(newFiltersState[fieldName].ignoreFilterValue, { checked: false })
            Object.assign(newFiltersState[fieldName].nullFilterValue, { checked: checked })
        }
        dispatch(setCustomMapFiltersState(newFiltersState))
        setItemWebStorage(`customMapFiltersState-${objectTypeName}`, newFiltersState)
        let newAppliedFilter = type === 'ignore' ? { [`ignore-${fieldName}`]: checked } : { [`null-${fieldName}`]: checked, [`ignore-${fieldName}`]: false }
        let newAppliedFilters = {
            ...appliedFilters,
            [fieldName]: {
                ...appliedFilters[fieldName],
                treeValues: {
                    ...appliedFilters[fieldName]?.treeValues,
                    ...newAppliedFilter
                }
            }
        }
        dispatch(setCustomMapAppliedFilters(newAppliedFilters))
        setItemWebStorage(`customMapAppliedFilters-${objectTypeName}`, newAppliedFilters)
    }

    function setSearchValue(value: string, fieldName: string) {
        let newFiltersState = structuredClone(filtersState)
        newFiltersState[fieldName].searchValue = value
        dispatch(setCustomMapFiltersState(newFiltersState))
        setItemWebStorage(`customMapFiltersState-${objectTypeName}`, newFiltersState)
    }

    async function fillInitFilterTreeCheckboxValues(authToken: any) {
        let newFiltersState = structuredClone(filtersState)
        const treeValuesFromSource = getItemWebStorage(`customMapFiltersTreeValues-${objectTypeName}`) ?? {}
        let newTreeValuesFromSource = structuredClone(treeValuesFromSource)
        const { treeValuesSource } = filtersState[fieldName]
        if (treeValuesSource) {
            let treeValues
            if (newTreeValuesFromSource[fieldName]) {
                treeValues = newTreeValuesFromSource[fieldName]
            }
            else {
                treeValues = await getTreeValuesFromSource(treeValuesSource, authToken)
                if (treeValues?.error === 401) {
                    return
                }
            }
            newFiltersState[fieldName].treeValues = treeValues
            newTreeValuesFromSource = { ...newTreeValuesFromSource, [fieldName]: treeValues }
        }
        setItemWebStorage(`customMapFiltersTreeValues-${objectTypeName}`, newTreeValuesFromSource)
        dispatch(setCustomMapFiltersState(newFiltersState))
        setItemWebStorage(`customMapFiltersState-${objectTypeName}`, newFiltersState)
    }

    async function getTreeValuesFromSource(treeValuesSource: any, authToken: any) {
        const { url, propertyForCaption, propertyForValue, propertyForParentData,
            propertyForParentCaption, fetchPlan, sort } = treeValuesSource
        let customUrl = url
        if (fetchPlan || sort) {
            let queryParameters = []
            if (fetchPlan) {
                queryParameters.push(`fetchPlan=${fetchPlan}`)
            }
            if (sort) {
                queryParameters.push(`sort=${sort}`)
            }
            customUrl += `?${queryParameters.join('&')}`
        }
        try {
            const response = await axios.get(customUrl, {
                headers: {
                    "Authorization": `Bearer ${authToken}`
                }
            });
            const valuesArray = response.data
            let treeValuesGroups = Object.groupBy(valuesArray, ({ [propertyForParentData]: { [propertyForParentCaption]: groupCaption } }: any) => groupCaption)
            let treeValues = Object.entries(treeValuesGroups).map(([groupCaption, treeValues]) => {
                return {
                    caption: groupCaption,
                    treeValues: treeValues?.map((treeItem: any) => {
                        return {
                            caption: treeItem[propertyForCaption],
                            value: treeItem[propertyForValue]
                        }
                    })
                }
            })
            return configureTreeCheckboxNodes(treeValues)
        } catch (error: any) {
            if (error?.response?.status === 401) {
                const restAuthRequestOptions = getItemWebStorage('currentConfig')?.mapAdditionalItems?.find((item: any) => item?.objectType === objectTypeName)?.openDataOptions
                await authorizeToCustomRest(restAuthRequestOptions)
                return { error: 401 }
            }
        }
    }

    return (
        <div className="filter-tree-checkbox">
            <div className="map-hint-filters-checkbox-div">
                <Checkbox
                    inputId={`ignore-${fieldName}`}
                    name={`ignore-${fieldName}`}
                    onChange={(event: any) => applyFilterTreeCheckboxIgnoreOrNullValue(event.checked, fieldName, 'ignore')}
                    checked={ignoreFilterValue?.checked}
                />
                <label htmlFor={`ignore-${fieldName}`} className="ms-2">{ignoreFilterValue.caption}</label>
            </div>
            {hasSearch &&
                <input onChange={(e: any) => setSearchValue(e.target.value, fieldName)} value={searchValue} className="tree-checkbox-search"></input>
            }
            <div className={"tree-checkbox with-search-container" + (ignoreFilterValue?.checked ? " filter-ignore" : '')}>
                {captionFilter(nullFilterValue.caption) &&
                    <div className="map-hint-filters-checkbox-div null-filter-value">
                        <Checkbox
                            inputId={`null-${fieldName}`}
                            name={`null-${fieldName}`}
                            onChange={(event: any) => applyFilterTreeCheckboxIgnoreOrNullValue(event.checked, fieldName, 'null')}
                            checked={nullFilterValue?.checked}
                        />
                        <label htmlFor={`null-${fieldName}`} className="ms-2">{nullFilterValue.caption}</label>
                    </div>
                }
                {treeValues
                    ?
                    <Tree
                        value={treeValues}
                        filter
                        onFilterValueChange={(e: any) => setSearchValue(e.value, fieldName)}
                        filterTemplate
                        filterValue={searchValue}
                        selectionMode="checkbox"
                        selectionKeys={selectedTreeValues}
                        onSelectionChange={(event) => applyFilterTreeCheckboxValues(event.value, fieldName)}
                    />
                    :
                    <div className="map-hint-filters-checkbox-div">
                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                        <span>Загрузка...</span>
                    </div>
                }
                {!treeCheckboxHasFilterValue(treeValues, searchValue, 0) && !captionFilter(nullFilterValue.caption) &&
                    <div className="map-hint-filters-checkbox-div">
                        <label className="no-results">Нет результатов</label>
                    </div>
                }
            </div>
        </div>
    )
}
